import {
  creditMultiplierFromPricing,
  findPriceAnchor,
  openRouterPricingPerMillion
} from './model-price.js';

/** Guests and free-tier users may only use styles at or below this credit multiplier. */
export const FREE_STYLE_MAX_MULTIPLIER = 0.25;
/** Trial/starter users may use Plus styles at or below this credit multiplier. */
export const STARTER_STYLE_MAX_MULTIPLIER = 1;

const PREMIUM_STYLE_TIERS = new Set(['single', 'harem_protagonist']);
const STARTER_STYLE_TIERS = new Set(['trial_offer', 'starter']);

/**
 * included ≤ 0.25x, plus ≤ 1x, max > 1x. Missing price is Max.
 * @param {number|null|undefined} multiplier
 * @returns {'included' | 'plus' | 'max'}
 */
export const accessLaneFromMultiplier = (multiplier) => {
  const value = typeof multiplier === 'number' ? multiplier : Number(multiplier);
  if (multiplier == null || multiplier === '' || !Number.isFinite(value)) return 'max';
  if (value <= FREE_STYLE_MAX_MULTIPLIER) return 'included';
  if (value <= STARTER_STYLE_MAX_MULTIPLIER) return 'plus';
  return 'max';
};

/**
 * @param {'included' | 'plus' | 'max'} lane
 * @param {string|null|undefined} membershipTier
 * @returns {boolean}
 */
export const isLaneAllowedForTier = (lane, membershipTier) => {
  const tier = String(membershipTier || 'free').trim().toLowerCase();
  if (lane === 'included') return true;
  if (lane === 'plus') return STARTER_STYLE_TIERS.has(tier) || PREMIUM_STYLE_TIERS.has(tier);
  return PREMIUM_STYLE_TIERS.has(tier);
};

/**
 * @param {number|null|undefined} multiplier
 * @param {string|null|undefined} membershipTier
 * @param {{ skipMembershipGate?: boolean }} [options]
 * @returns {boolean}
 */
export const isMultiplierAllowedForMembership = (multiplier, membershipTier, { skipMembershipGate = false } = {}) => {
  if (skipMembershipGate) return true;
  return isLaneAllowedForTier(accessLaneFromMultiplier(multiplier), membershipTier);
};

/**
 * Full site and a saved OpenRouter key skip Plus/Max locks.
 * @param {{ sitePolicy?: string, usesUserOpenRouterKey?: boolean }} [options]
 * @returns {boolean}
 */
export const skipsModelLaneGate = ({ sitePolicy = 'sfw', usesUserOpenRouterKey = false } = {}) =>
  sitePolicy === 'full' || usesUserOpenRouterKey === true;

/**
 * Credit multiplier for an OpenRouter per-token price against the 1x style anchor.
 * @param {object|null|undefined} pricing
 * @param {Array<object>|null|undefined} styles
 * @returns {number|null}
 */
export const multiplierForOpenRouterPricing = (pricing, styles) => {
  const perMillion = openRouterPricingPerMillion(pricing);
  return creditMultiplierFromPricing(perMillion, findPriceAnchor(styles));
};

/**
 * Lane to reject, or null when the model is allowed.
 * Unknown price is Max.
 * @param {object|null|undefined} pricing OpenRouter per-token pricing
 * @param {Array<object>|null|undefined} styles
 * @param {string|null|undefined} membershipTier
 * @param {boolean} [skipMembershipGate]
 * @returns {'plus' | 'max' | null}
 */
export const forbiddenLaneForOpenRouterPricing = (pricing, styles, membershipTier, skipMembershipGate = false) => {
  const multiplier = multiplierForOpenRouterPricing(pricing, styles);
  if (isMultiplierAllowedForMembership(multiplier, membershipTier, { skipMembershipGate })) return null;
  const lane = accessLaneFromMultiplier(multiplier);
  return lane === 'plus' ? 'plus' : 'max';
};

export class ModelLaneForbiddenError extends Error {
  /**
   * @param {'plus' | 'max'} lane
   */
  constructor(lane) {
    const normalized = lane === 'max' ? 'max' : 'plus';
    super(normalized === 'max'
      ? 'Max models are included with Premium.'
      : 'Plus models are included with Starter.');
    this.name = 'ModelLaneForbiddenError';
    this.code = 'model_lane_forbidden';
    this.lane = normalized;
    this.status = 403;
  }
}
